<script setup>
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from 'vue'
import api from '@/services/api'

const props = defineProps({
    label: {
        type: String,
        required: true
    },
    category: {
        type: String,
        required: false,
        default: ''
    },
    modelValue: {
        type: [String, Number, Object],
        required: false,
        default: ''
    },
    apiurl: {
        type: String,
        required: false,
        default: ''
    },
    canEditAndAdd: {
        type: Boolean,
        required: true
    },
    localData: {
        type: Array,
        required: false,
        default: () => []
    }
})

const emit = defineEmits(['update:modelValue'])

const normalizeString = (str = '') => String(str).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

const buildItemName = (item = {}) => {
    const code = item.code || item.clave || item.key || item.codigo;
    const text = item.name || item.description || item.value || item.title;
    if (code && text) return `${code} - ${text}`;
    if (text) return text;
    if (code) return String(code);
    return '';
};

const normalizeData = (data = []) => {
    return data.filter(Boolean).map(item => ({
        id: item.id,
        name: buildItemName(item),
        normalizedName: normalizeString(buildItemName(item)),
        deleted_at: item.deleted_at || null,
        category: item.category || null,
    }));
}

const normalizeModelValue = (val) => {
    if (val === null || val === undefined) return { id: '', label: '' };
    if (typeof val === 'object') {
        // Si ya tiene la propiedad 'label', usarla directamente
        if ('label' in val && val.label) {
            return { id: val.id || '', label: val.label };
        }
        // Si no, intentar construir el nombre de otras propiedades
        const id = val.id || val.key || val.code || val.codigo || val.clave || '';
        const label = buildItemName(val) || String(id);
        return { id, label };
    }
    return { id: val, label: '' };
};

const saveToLocalStorage = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data));
}

const loadFromLocalStorage = (key) => {
    const data = localStorage.getItem(key);
    const parsed = data ? JSON.parse(data) : [];
    return Array.isArray(parsed) ? parsed : [];
}

const allMenuItems = ref([]) // Almacena todos los datos
const menuItems = ref([]) // Almacena los datos filtrados y paginados
const newItemName = ref('')
const initialModel = normalizeModelValue(props.modelValue)
const selectedItemId = ref(initialModel.id || '')
const selectedItemName = ref(initialModel.label || '')
const searchQuery = ref('')
const dropdown = ref(null)
const dropdownUp = ref(false)
const dropdownLeft = ref(false)
const dropdownRight = ref(false)
const dropdownWidth = ref('300px')
const isEditing = ref(false)
const editingItemId = ref(null)
const isDropdownOpen = ref(false)
const isLoadingMore = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(100)
const totalPages = ref(1)
const scrollableOptions = ref(null)

const handleClickOutside = (event) => {
    if (dropdown.value && !dropdown.value.contains(event.target)) {
        isDropdownOpen.value = false
    }
}

const fetchAllMenuValues = async (page = 1) => {
    const storageKey = `${props.apiurl}_${props.category}`;
    if (props.localData.length > 0) {
        allMenuItems.value = normalizeData(props.localData);
        totalPages.value = 1; // Solo una página para datos locales
        updateMenuItems();
        if (selectedItemId.value && !selectedItemName.value) await findSelectedItem(selectedItemId.value);
    } else if (props.apiurl) {
        try {
            isLoadingMore.value = true;
            let url = `/${props.apiurl}?page=${page}&per_page=${itemsPerPage.value}`;
            if (props.category) url += `&category=${props.category}`;
            if (searchQuery.value) url += `&search=${normalizeString(searchQuery.value)}`;

            const response = await api.get(url);
            const newItems = normalizeData(response.data.data);
            allMenuItems.value = [...allMenuItems.value, ...newItems];

            saveToLocalStorage(storageKey, allMenuItems.value);
            totalPages.value = response.data.pagination.last_page;

            if (selectedItemId.value && !allMenuItems.value.find(item => String(item.id) === String(selectedItemId.value))) {
                try {
                    const selectedItemResponse = await api.get(`/${props.apiurl}/${selectedItemId.value}`);
                    const selectedItemData = normalizeData([selectedItemResponse.data.data]);
                    allMenuItems.value.push(...selectedItemData);
                } catch (err) {
                    console.error('Error fetching selected item:', err);
                }
            }

            updateMenuItems();
            if (selectedItemId.value && !selectedItemName.value) {
                await findSelectedItem(selectedItemId.value);
            }
        } catch (error) {
            console.error('Error fetching menu values:', error);
        } finally {
            isLoadingMore.value = false;
        }
    }
};

const updateMenuItems = () => {
    const filteredItems = allMenuItems.value.filter(item => {
        const notDeleted = item.deleted_at === null || item.deleted_at === undefined;
        return notDeleted && item.normalizedName.includes(normalizeString(searchQuery.value));
    });

    menuItems.value = filteredItems;

    // Asegúrate de que el valor seleccionado esté presente aunque esté marcado como eliminado
    if (selectedItemId.value) {
        const selectedItem = allMenuItems.value.find(item => String(item.id) === String(selectedItemId.value));
        if (selectedItem && selectedItem.deleted_at !== null && !menuItems.value.some(item => item.id === selectedItem.id)) {
            menuItems.value.unshift(selectedItem);
        }
    }
};

const findSelectedItem = async (rawId) => {
    const { id, label } = normalizeModelValue(rawId);
    selectedItemId.value = id || '';
    
    // Si ya tenemos el label, usarlo directamente sin hacer llamada a la API
    if (label) {
        selectedItemName.value = label;
        return;
    }
    
    if (!id) {
        selectedItemName.value = '';
        return;
    }

    // Buscar en items ya cargados
    let selectedItem = allMenuItems.value.find(item => String(item.id) === String(id));

    // Solo intentar llamar a la API si no está en cache y tenemos la URL
    if (!selectedItem && props.apiurl) {
        try {
            const response = await api.get(`/${props.apiurl}/${id}`);
            const itemData = normalizeData([response.data.data])[0];
            const existingIndex = allMenuItems.value.findIndex(item => item.id === itemData.id);
            if (existingIndex === -1) {
                allMenuItems.value.push(itemData);
            } else {
                allMenuItems.value[existingIndex] = itemData;
            }
            const storageKey = `${props.apiurl}_${props.category}`;
            saveToLocalStorage(storageKey, allMenuItems.value);
            selectedItem = itemData;
        } catch (error) {
            console.error('Error fetching selected item:', error);
            // Si falla, usar el ID como nombre
            selectedItemName.value = String(id);
            return;
        }
    }

    selectedItemName.value = selectedItem ? (selectedItem.name || String(id)) : (label || String(id));
}

const selectItem = (item) => {
    selectedItemId.value = item.id;
    selectedItemName.value = item.name || item.id;
    emit('update:modelValue', item.id);
}

const handleItemClick = (item) => {
    selectItem(item);
    closeDropdown();
}

const editItem = (item) => {
    newItemName.value = item.name;
    isEditing.value = true;
    editingItemId.value = item.id;
}

const updateItem = async () => {
    if (!newItemName.value.trim()) return;
    try {
        const response = await api.put(`/${props.apiurl}/${editingItemId.value}`, {
            name: newItemName.value,
            category: props.category
        });
        const updatedItem = response.data.data;

        const index = allMenuItems.value.findIndex(item => item.id === editingItemId.value);
        if (index !== -1) {
            allMenuItems.value[index] = normalizeData([updatedItem])[0];
        }

        // Actualizar en localStorage por apiurl y categoría
        const storageKey = `${props.apiurl}_${props.category}`;
        saveToLocalStorage(storageKey, allMenuItems.value);

        if (selectedItemId.value === editingItemId.value) {
            selectedItemName.value = updatedItem.name;
        }

        resetEditState();
        updateMenuItems();
    } catch (error) {
        console.error('Error updating item:', error);
    }
};

const addItem = async () => {
    if (!newItemName.value.trim()) return;

    // Verificar si el valor ya existe en los datos locales
    let existingItem = allMenuItems.value.find(item => item.normalizedName === normalizeString(newItemName.value.trim()));
    if (existingItem) {
        // Si el ítem existe en el localStorage y está marcado como eliminado, restaurarlo
        if (existingItem.deleted_at !== null) {
            try {
                await api.post(`/${props.apiurl}/${existingItem.id}/restore`);
                existingItem.deleted_at = null;
                const storageKey = `${props.apiurl}_${props.category}`;
                saveToLocalStorage(storageKey, allMenuItems.value); // Actualizar localStorage
            } catch (error) {
                console.error('Error restoring item from API:', error);
            }
        }
        // Seleccionar el valor existente y finalizar
        selectItem(existingItem);
        resetEditState();
        return;
    }

    // Verificar si el valor ya existe en la base de datos
    try {
        let url = `/${props.apiurl}?search=${normalizeString(newItemName.value.trim())}`;
        if (props.category) url += `&category=${props.category}`;

        const response = await api.get(url);
        const apiItems = normalizeData(response.data.data);
        existingItem = apiItems.find(item => item.normalizedName === normalizeString(newItemName.value.trim()));

        if (existingItem) {
            // Si el ítem existe en el API y está eliminado, restaurarlo
            if (existingItem.deleted_at !== null) {
                await api.post(`/${props.apiurl}/${existingItem.id}/restore`);
                existingItem.deleted_at = null;
            }

            // Agregar el ítem restaurado a la lista y seleccionarlo
            allMenuItems.value.push(existingItem);
            const storageKey = `${props.apiurl}_${props.category}`;
            saveToLocalStorage(storageKey, allMenuItems.value); // Actualizar localStorage
            selectItem(existingItem);
            resetEditState();
            return;
        }
    } catch (error) {
        console.error('Error checking existing item in API:', error);
    }

    // Si no existe en localStorage ni en el API, crear un nuevo valor
    try {
        const response = await api.post(`/${props.apiurl}`, {
            name: newItemName.value,
            category: props.category
        });
        const newItem = response.data.data;
        allMenuItems.value.push(normalizeData([newItem])[0]);

        // Guardar en localStorage por apiurl y categoría y ordenar alfabéticamente
        const storageKey = `${props.apiurl}_${props.category}`;
        allMenuItems.value.sort((a, b) => a.normalizedName.localeCompare(b.normalizedName));
        saveToLocalStorage(storageKey, allMenuItems.value);

        selectItem(newItem); // Seleccionar el nuevo elemento automáticamente
        resetEditState();
        updateMenuItems();
    } catch (error) {
        console.error('Error adding item:', error);
    }
};

const resetEditState = () => {
    newItemName.value = '';
    isEditing.value = false;
    editingItemId.value = null;
    isDropdownOpen.value = false;
}

const handleDelete = (id) => {
    if (confirm('¿Estás seguro de que deseas eliminar este elemento?')) {
        deleteItem(id);
    }
}

const deleteItem = async (id) => {
    try {
        await api.delete(`/${props.apiurl}/${id}`);
        const itemToDelete = allMenuItems.value.find(item => item.id === id);
        if (itemToDelete) {
            itemToDelete.deleted_at = new Date().toISOString();
        }

        // Actualizar en localStorage por apiurl y categoría
        const storageKey = `${props.apiurl}_${props.category}`;
        saveToLocalStorage(storageKey, allMenuItems.value);

        // Mantener el valor seleccionado aunque haya sido eliminado
        if (selectedItemId.value === id) {
            selectedItemName.value = itemToDelete.name;
        }

        updateMenuItems();
    } catch (error) {
        console.error('Error deleting item:', error);
    }
}

const toggleDropdown = async () => {
    dropdownWidth.value = `${dropdown.value.offsetWidth}px`;
    isDropdownOpen.value = !isDropdownOpen.value;
    if (isDropdownOpen.value) {
        currentPage.value = 1;
        allMenuItems.value = [];
        await fetchAllMenuValues();
        await nextTick();
    }
}

const closeDropdown = () => {
    isDropdownOpen.value = false;
}

const onScroll = async () => {
    if (scrollableOptions.value) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableOptions.value;
        if (scrollTop + clientHeight >= scrollHeight - 10 && currentPage.value < totalPages.value) {
            currentPage.value += 1;
            await fetchAllMenuValues(currentPage.value);
        }
    }
};

onMounted(async () => {
    const storageKey = `${props.apiurl}_${props.category}`;
    allMenuItems.value = normalizeData(loadFromLocalStorage(storageKey));

    if (allMenuItems.value.length === 0) {
        await fetchAllMenuValues();
    }

    await findSelectedItem(selectedItemId.value || props.modelValue);

    document.addEventListener('click', handleClickOutside);
});


onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});

watch(() => props.modelValue, async (newValue, oldValue) => {
    if (newValue !== oldValue) {
        await findSelectedItem(newValue);
    }
}, { immediate: true });

watch(searchQuery, () => {
    currentPage.value = 1;
    allMenuItems.value = [];
    fetchAllMenuValues();
});

watch(allMenuItems, () => {
    updateMenuItems();
    if (selectedItemId.value && !selectedItemName.value) {
        const sel = allMenuItems.value.find(item => String(item.id) === String(selectedItemId.value));
        if (sel) selectedItemName.value = sel.name || sel.id;
    }
});

watch(() => props.apiurl, async (newApiUrl, oldApiUrl) => {
    if (newApiUrl !== oldApiUrl) {
        const storageKey = `${props.apiurl}_${props.category}`;
        localStorage.removeItem(storageKey);
        allMenuItems.value = [];
        menuItems.value = [];
        selectedItemId.value = '';
        selectedItemName.value = '';
        await fetchAllMenuValues();
    }
});

const clearLocalStorage = () => {
    localStorage.clear();
    allMenuItems.value = [];
    menuItems.value = [];
    selectedItemId.value = '';
    selectedItemName.value = '';
    alert('LocalStorage eliminado.');
}
</script>

<template>
    <button class="btn btn-danger mt-3 hidden" @click="clearLocalStorage">
        Eliminar Todo el LocalStorage
    </button>
    <div class="m-0">
        <label v-if="label" for="menu-selected2">{{ label }}</label>
        <div ref="dropdown" class="dropdown">
            <button
id="dropdownMenuButton"
                class="form-control dropdown-toggle d-flex justify-content-between align-items-center text-start"
                type="button" :class="{ show: isDropdownOpen }" @click="toggleDropdown">
                <span class="truncated-text">{{ selectedItemName || selectedItemId || 'Seleccionar' }}</span>
            </button>
            <ul
class="dropdown-menu custom-dropdown-menu"
                :class="{ 'dropdown-menu-up': dropdownUp, 'dropdown-menu-left': dropdownLeft, 'dropdown-menu-right': dropdownRight, show: isDropdownOpen }"
                :style="{ width: `${dropdownWidth}px` }" aria-labelledby="dropdownMenuButton">

                <!-- Header fijo con el campo de búsqueda -->
                <li class="px-3 fixed-header">
                    <input v-model="searchQuery" placeholder="Buscar..." class="form-control mb-2" />
                </li>

                <!-- Contenedor desplazable para los elementos del menú -->
                <div ref="scrollableOptions" class="scrollable-options" @scroll="onScroll">
                    <li
v-for="item in menuItems" :key="item.id"
                        :class="{ 'selected-item': String(selectedItemId) === String(item.id) }"
                        class="d-flex justify-content-between align-items-center p-1 menu-item">
                        <span
class="flex-grow-1 ms-3 text-start" style="flex-shrink: 0; width: 70%;"
                            @click="handleItemClick(item)">{{ item.name || item.id }}</span>
                        <a
v-if="canEditAndAdd" class="btn-link p-1 text-primary me-0" style="cursor: pointer;"
                            @click.prevent="editItem(item)">
                            <i class="fas fa-edit"></i>
                        </a>
                        <a
v-if="canEditAndAdd" class="btn-link p-1 text-danger me-3" style="cursor: pointer;"
                            @click.prevent="handleDelete(item.id)">
                            <i class="fas fa-trash-alt"></i>
                        </a>
                    </li>
                    <li v-if="isLoadingMore" class="text-center p-2">Cargando más...</li>
                </div>

                <!-- Footer fijo con el campo para agregar nuevos elementos -->
                <li v-if="canEditAndAdd" class="dropdown-item fixed-footer">
                    <div class="d-flex align-items-center btn-create-update">
                        <input v-model="newItemName" placeholder="Nuevo valor" class="form-control" />
                        <button class="btn btn-primary ms-2 mt-3" @click="isEditing ? updateItem() : addItem()">
                            {{ isEditing ? 'Guardar' : 'Agregar' }}
                        </button>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
.menu-item:hover {
    background-color: hsl(0, 0%, 95%);
}

.selected-item {
    background-color: #eeeeee;
}

.selected-item:hover {
    background-color: #eeeeee;
}

.dropdown-menu {
    width: 100%;
    max-height: auto;
    overflow: hidden;
    z-index: 9000 !important;
    display: flex;
    flex-direction: column;
}

.scrollable-options {
    max-height: 180px;
    /* Ajusta esta altura si el footer es más grande */
    overflow-y: auto;
    flex-grow: 1;
    padding-bottom: 10px;
    /* Espacio para evitar superposición */
}

.fixed-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
}

.fixed-footer {
    background: white;
    padding: 10px;
    border-top: 1px solid #ddd;
}

.truncated-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.btn-create-update {
    padding: 0px;
    margin-top: -15px;
    margin-bottom: -20px;
}

.hidden {
    display: none;
}
</style>
